/** Iter 639 — staff till split · live session list: waiting-customer
 * banners → ➕ New sale row → live list → sound toggle.
 * NewSaleForm is folded in here (Iter 639 adj.); SessionRow stays its
 * own file. Takes the till bag `{ t }` (auth-lost → t.logout). */
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Receipt, Volume2, VolumeX } from "lucide-react";
import { tillApi, detail, playChime } from "@/features/staffTill/helpers";
import SessionRow from "@/features/staffTill/SessionRow";

export default function SessionsCard({ t }) {
  // Iter-325 chime + Iter-462 follow-up — soft cues from the 5s poll so
  // busy staff never miss anything: new session = two-tone ding, a
  // payment landing (status flips to paid) = brighter triad + toast.
  // First load never chimes; the toggle sticks for the shift via
  // sessionStorage. Pairs with TillOfflineBar: while offline the poll
  // fails and the snapshot freezes, so the first refetch after
  // reconnect chimes once for everything that landed during the outage.
  const [muted, setMuted] = useState(() => sessionStorage.getItem("till_muted") === "1");
  const prevSnap = useRef(null); // Map(session id → was paid)
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const q = useQuery({
    queryKey: ["till-sessions"],
    refetchInterval: 5_000,
    retry: 0,
    queryFn: async () => {
      try {
        return (await tillApi.get("/classifieds/engagement/till/sessions")).data;
      } catch (err) {
        if (err?.response?.status === 401) t.logout();
        throw err;
      }
    },
  });
  const sessions = q.data?.sessions || [];

  useEffect(() => {
    if (!q.data) return;
    const list = q.data.sessions || [];
    const snap = new Map(list.map((s) => [s.id, /paid|resolved/.test(s.status || "")]));
    if (prevSnap.current) {
      let hasNewSession = false;
      const paidCodes = [];
      list.forEach((s) => {
        const wasThere = prevSnap.current.has(s.id);
        const isPaid = snap.get(s.id);
        if (!wasThere) hasNewSession = true;
        if (isPaid && (!wasThere || !prevSnap.current.get(s.id))) {
          paidCodes.push(s.short_code || "session");
        }
      });
      if (paidCodes.length > 0) {
        if (!mutedRef.current) playChime("payment");
        toast.success(`Payment received — ${paidCodes.join(", ")}`);
      } else if (hasNewSession && !mutedRef.current) {
        playChime("session");
      }
    }
    prevSnap.current = snap;
  }, [q.data]);

  const toggleMute = () => {
    setMuted((m) => {
      sessionStorage.setItem("till_muted", m ? "0" : "1");
      return !m;
    });
  };

  const soundToggle = (
    <div className="mt-4 flex justify-end">
      <button
        type="button"
        onClick={toggleMute}
        data-testid="till-sound-toggle"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800"
      >
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        {muted ? "Sound off" : "Sound on"}
      </button>
    </div>
  );

  // Iter-550 — customers who scanned while no sale exists (server-expiring).
  const waitingBanners = (q.data?.waiting_customers || []).map((w, i) => (
    <div
      key={`${w.name || "guest"}-${i}`}
      data-testid={`till-waiting-customer-${i}`}
      className="mt-2 rounded-xl bg-amber-50 border border-amber-300 px-4 py-2.5 text-sm font-semibold text-amber-800"
    >
      👋 {w.name || "A customer"} is at the counter waiting — enter the bill and press Start sale.
    </div>
  ));

  if (q.isLoading) {
    return <div className="mt-8 grid place-items-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  return (
    <>
      {waitingBanners}
      <NewSaleForm />
      {sessions.length === 0 ? (
        <div className="mt-2 rounded-2xl bg-white shadow-card p-8 text-center" data-testid="till-sessions-empty">
          <Receipt className="w-7 h-7 mx-auto text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No live sessions — start a new sale above, then ask the customer to scan your QR.</p>
        </div>
      ) : (
        <ul className="mt-2 space-y-3" data-testid="till-sessions-list">
          {sessions.map((s) => <SessionRow key={s.id} session={s} />)}
        </ul>
      )}
      {soundToggle}
    </>
  );
}

function NewSaleForm() {
  // Iter-520/528 — TILL-FIRST: a new sale starts HERE (staff enters the
  // bill), then the customer scans the store QR to claim it.
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [inlineErr, setInlineErr] = useState(null);
  const startMut = useMutation({
    mutationFn: async (v) =>
      (await tillApi.post("/classifieds/engagement/till/sessions", { bill_amount: v })).data,
    onSuccess: (data) => {
      setAmount("");
      setInlineErr(null);
      toast.success(data?.message || "New sale started — ask the customer to scan the store QR");
      qc.invalidateQueries({ queryKey: ["till-sessions"] });
    },
    onError: (err) => {
      const msg = detail(err, "Couldn't start the sale");
      // 409 = bill below the program minimum — show it inline.
      if (err?.response?.status === 409) setInlineErr(msg);
      else toast.error(msg);
    },
  });
  return (
    <form
      className="mt-6 rounded-2xl bg-white shadow-card p-5"
      data-testid="till-new-sale-form"
      onSubmit={(e) => {
        e.preventDefault();
        const v = parseFloat(amount);
        if (!Number.isFinite(v) || v <= 0) return toast.error("Enter the bill amount first.");
        startMut.mutate(v);
      }}
    >
      <div className="flex items-center gap-2 text-sm font-black text-slate-900">
        <Plus className="w-4 h-4" /> New sale
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
          <input
            value={amount}
            onChange={(e) => { setAmount(e.target.value.replace(/[^\d.]/g, "")); setInlineErr(null); }}
            placeholder="0.00"
            inputMode="decimal"
            data-testid="till-new-sale-input"
            className="w-full rounded-full border border-slate-300 pl-7 pr-4 py-2.5 text-sm focus:outline-none focus:border-slate-500"
          />
        </div>
        <button
          type="submit"
          disabled={startMut.isPending}
          data-testid="till-new-sale-start"
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-5 py-2.5 disabled:opacity-60 transition-colors"
        >
          {startMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Start"}
        </button>
      </div>
      {inlineErr && (
        <div className="mt-2 text-xs font-semibold text-red-600" data-testid="till-new-sale-error">{inlineErr}</div>
      )}
      <p className="mt-2 text-[11px] text-slate-400">The customer scans your store QR to pick this sale up.</p>
    </form>
  );
}
